import apiClient from './api';
import {
  BulkImportJobStatus,
  StartBulkImportResponse,
  BulkImportJob,
  BulkImportOptions,
} from '@/types/bulkImport';

/**
 * Bulk Import Service
 *
 * API calls for bulk post import functionality
 */

export const bulkImportService = {
  /**
   * Start bulk import by uploading file
   */
  startImport: async (
    file: File,
    options?: BulkImportOptions,
    importType: 'posts' | 'variations' = 'posts'
  ): Promise<StartBulkImportResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('importType', importType);

    // Send as comma-separated strings (not JSON.stringify)
    if (options?.defaultLabels && options.defaultLabels.length > 0) {
      formData.append('defaultLabels', options.defaultLabels.join(','));
    }
    if (options?.defaultHashtags && options.defaultHashtags.length > 0) {
      formData.append('defaultHashtags', options.defaultHashtags.join(','));
    }
    if (options?.defaultChannelIds && options.defaultChannelIds.length > 0) {
      formData.append('defaultChannelIds', options.defaultChannelIds.join(','));
    }

    const response = await apiClient.post('/api/posts/bulk-import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    // Extract jobId from nested response: { success, message, data: { job: { id } } }
    return {
      jobId: response.data.data.job.id,
      message: response.data.message,
    };
  },

  /**
   * Get job status with progress
   */
  getJobStatus: async (jobId: number): Promise<BulkImportJobStatus> => {
    const response = await apiClient.get(`/api/posts/bulk-import/${jobId}`);
    return response.data.data;
  },

  /**
   * Cancel a running job
   */
  cancelJob: async (jobId: number): Promise<void> => {
    await apiClient.post(`/api/posts/bulk-import/${jobId}/cancel`);
  },

  /**
   * Download CSV/XLSX template
   */
  downloadTemplate: async (format: 'csv' | 'xlsx', importType: 'posts' | 'variations' = 'posts'): Promise<Blob> => {
    const response = await apiClient.get(`/api/posts/bulk-import/template/${format}`, {
      params: { type: importType },
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Get user's import job history
   */
  getJobs: async (): Promise<BulkImportJob[]> => {
    const response = await apiClient.get('/api/posts/bulk-import/jobs');
    return response.data.jobs;
  },

  /**
   * Export variations template for selected posts
   */
  exportVariationsTemplate: async (postIds: number[]): Promise<void> => {
    const response = await apiClient.post(
      '/api/posts/variations/export-template',
      { postIds },
      { responseType: 'blob' }
    );

    // Download as Excel file
    const blob = response.data;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const timestamp = new Date().toISOString().split('T')[0];
    a.download = `BulkVariations-${timestamp}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },
};
